import * as React from "react";

import { getQuerySorting, getQueryFilters } from "../ReducerUtilities";
import { QueryOrderDirection, Query, User, QueryCondition } from "../../../api/apiTypes";

export interface QueryTableState {
    query: Query<any>;
}


export enum ActionType {
    PageQueryTable = "PageQueryTable",
    SetPageSizeQueryTable = "SetPageSizeQueryTable",
    SortQueryTable = "SortQueryTable",
    FilterQueryTable = "FilterQueryTable",
    ClearFiltersQueryTable = "ClearFiltersQueryTable",
    ClearFilterQueryTable = "ClearFilterQueryTable",
    ReceiveQueryTable = "ReceiveQueryTable",
}

export type QueryTableActions = PageAction | SetPageSizeAction | SortAction | FilterAction | ClearFiltersAction | ClearFilterAction | ReceiveQueryAction;

export type QueryTableReducer = React.Reducer<QueryTableState, QueryTableActions>;

export class PageAction {
    type: ActionType.PageQueryTable;
    page: number;

    static create(page: number): PageAction {
        return {
            type: ActionType.PageQueryTable,
            page: page
        };
    }
}

export class SetPageSizeAction {
    type: ActionType.SetPageSizeQueryTable;
    pageSize: number;

    static create(pageSize: number): SetPageSizeAction {
        return {
            type: ActionType.SetPageSizeQueryTable,
            pageSize: pageSize
        };
    }
}

export class SortAction {
    type: ActionType.SortQueryTable;
    member: string;

    static create(member: string): SortAction {
        return {
            type: ActionType.SortQueryTable,
            member: member
        };
    }
}

export class FilterAction {
    type: ActionType.FilterQueryTable;
    member: string;
    condition: QueryCondition;
    values: any[];

    static create(member: string, condition: QueryCondition, values: any[]): FilterAction {
        return {
            type: ActionType.FilterQueryTable,
            member: member,
            condition: condition,
            values: values,
        };
    }
}

export class ClearFiltersAction {
    type: ActionType.ClearFiltersQueryTable;

    static create(): ClearFiltersAction {
        return {
            type: ActionType.ClearFiltersQueryTable,
        };
    }
}

export class ClearFilterAction {
    type: ActionType.ClearFilterQueryTable;
    member: string;

    static create(member: string): ClearFilterAction {
        return {
            type: ActionType.ClearFilterQueryTable,
            member: member
        };
    }
}

export class ReceiveQueryAction {
    type: ActionType.ReceiveQueryTable;
    query: Query<any>;

    static create(query: Query<any>): ReceiveQueryAction {
        return {
            type: ActionType.ReceiveQueryTable,
            query: query,
        };
    }
}

export const useQueryTable = (initialState: QueryTableState) => {

    const reducer: QueryTableReducer = (state: QueryTableState, action: QueryTableActions): QueryTableState => {
        switch (action.type) {
            case ActionType.SetPageSizeQueryTable:
                return {
                    ...state,
                    query: {
                        ...state.query,
                        Page: {
                            ...state.query.Page,
                            Length: action.pageSize,
                        }
                    }
                };
            case ActionType.PageQueryTable:
                return {
                    ...state,
                    query: {
                        ...state.query,
                        Page: {
                            ...state.query.Page,
                            Current: action.page
                        }
                    }
                };
            case ActionType.SortQueryTable:
                const order = getQuerySorting(state.query, action.member);

                return {
                    ...state,
                    query: {
                        ...state.query,
                        Order: order,
                    }

                };
            case ActionType.FilterQueryTable:
                const filters = getQueryFilters(
                    state.query,
                    action.member,
                    action.condition,
                    action.values
                );

                return {
                    ...state,
                    query: {
                        ...state.query,
                        Filters: filters,
                        Page: {
                            ...state.query.Page,
                            Current: 1
                        }
                    }
                };
            case ActionType.ReceiveQueryTable:
                return {
                    ...state,
                    query: {
                        ...state.query,
                        Page: action.query.Page,
                        Data: action.query.Data,
                    },
                };
            case ActionType.ClearFiltersQueryTable:
                return {
                    ...state,
                    query: {
                        ...state.query,
                        Filters: [],
                    },
                };
            case ActionType.ClearFilterQueryTable:
                return {
                    ...state,
                    query: {
                        ...state.query,
                        Filters: state.query.Filters.filter((item) => item.Member !== action.member),
                    },
                };
            default:
                throw new Error("Unexpected Query Table action");
        }
    }

    return React.useReducer(reducer, initialState);
}
